import { DateOnly } from '@if/core/date'
import { EOriginSystemId, EPaymentFrequency } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'

const SCOPE = 'worksheetFormDefaults'

export interface WorksheetFormDefaultsRequestDto {
  creditApplicationId: string
  originSystemId: EOriginSystemId
}

export interface WorksheetFormDefaultsSimulationRequestDto {
  merchantId: string
  stateIso: string
  amountRequested: number
  interestRate: number
  maxPmtAmount: number
  hasCoapplicant: boolean
  paymentFrequency?: string
  includeInsurance: boolean
  originSystemId: EOriginSystemId
}

export interface WorksheetFormDefaults {
  amountRequested: number
  paymentPlanId: string
  term: number | null
  paymentFrequency: EPaymentFrequency
  activationDate: DateOnly
  firstPaymentDate: DateOnly | null
  includeProtection: boolean
  isHighEstimate: boolean
}

interface WorksheetFormFieldDefaultsResponse {
  amountRequested: number
  paymentPlanId: string
  term: number | null
  paymentFrequency: EPaymentFrequency
  activationDate: string
  firstPaymentDate: string | null
  includeProtection: boolean
  isHighEstimate: boolean
}

interface WorksheetFormDefaultsResponse {
  defaults: WorksheetFormFieldDefaultsResponse
}

export const worksheetFormDefaultsQueryKeys = {
  all: [SCOPE] as const,
  defaults: (financingProgramId: string, dto: WorksheetFormDefaultsRequestDto) =>
    [SCOPE, 'app', financingProgramId, dto] as const,
  simulation: (financingProgramId: string, dto: WorksheetFormDefaultsSimulationRequestDto) =>
    [SCOPE, 'simulation', financingProgramId, dto] as const,
}

const transformDefaults = (r: WorksheetFormFieldDefaultsResponse): WorksheetFormDefaults => ({
  amountRequested: r.amountRequested,
  paymentPlanId: r.paymentPlanId,
  term: r.term,
  paymentFrequency: r.paymentFrequency,
  activationDate: new DateOnly(r.activationDate),
  firstPaymentDate: r.firstPaymentDate ? new DateOnly(r.firstPaymentDate) : null,
  includeProtection: r.includeProtection,
  isHighEstimate: r.isHighEstimate,
})

const fetchWorksheetFormDefaults = async (
  financingProgramId: string,
  dto: WorksheetFormDefaultsRequestDto,
): Promise<WorksheetFormDefaults> => {
  const response = await getApiClient().post<WorksheetFormDefaultsResponse>(
    `/Applications/${encodeURIComponent(financingProgramId)}/WorksheetFormDefaults`,
    dto,
  )
  return transformDefaults(response.data.defaults)
}

const fetchWorksheetFormDefaultsSimulation = async (
  financingProgramId: string,
  dto: WorksheetFormDefaultsSimulationRequestDto,
): Promise<WorksheetFormDefaults> => {
  const response = await getApiClient().post<WorksheetFormDefaultsResponse>(
    `/Applications/${encodeURIComponent(financingProgramId)}/WorksheetFormDefaults/Simulation`,
    dto,
  )
  return transformDefaults(response.data.defaults)
}

export const worksheetFormDefaultsQuery = (financingProgramId: string, dto: WorksheetFormDefaultsRequestDto) => ({
  queryKey: worksheetFormDefaultsQueryKeys.defaults(financingProgramId, dto),
  queryFn: () => fetchWorksheetFormDefaults(financingProgramId, dto),
})

export const worksheetFormDefaultsSimulationQuery = (
  financingProgramId: string,
  dto: WorksheetFormDefaultsSimulationRequestDto,
) => ({
  queryKey: worksheetFormDefaultsQueryKeys.simulation(financingProgramId, dto),
  queryFn: () => fetchWorksheetFormDefaultsSimulation(financingProgramId, dto),
})
